import React, { useMemo } from 'react'
import { NoteContextType } from '@dataTypes/note.type'
import './TagBadge.css'

export { NoteContextType }

/** Display labels for note context types (single source of truth for UI labels). */
const CONTEXT_TYPE_LABELS: Record<NoteContextType, string> = {
  [NoteContextType.APPOINTMENT]: 'Appointment',
  [NoteContextType.BILLING]: 'Billing',
  [NoteContextType.CHARTING]: 'Charting',
  [NoteContextType.TREATMENT_PLAN]: 'Tx plan',
  [NoteContextType.CALL]: 'Call',
  [NoteContextType.CONTACT]: 'Contact',
  [NoteContextType.PATIENT]: 'General',
  [NoteContextType.PERIO]: 'Perio',
  [NoteContextType.CLAIMS]: 'Claims',
  [NoteContextType.CLAIM_ITEM]: 'Claims',
  [NoteContextType.CAMPAIGN]: 'Campaign',
  [NoteContextType.FILES]: 'Files',
  [NoteContextType.XRAY]: 'X-ray File',
  [NoteContextType.INSURANCE]: 'Insurance',
  [NoteContextType.PROVIDER]: 'Provider',
  [NoteContextType.FORMS]: 'Forms',
  [NoteContextType.CARRIER]: 'Carrier',
}

/* Match AddNote modal badge colors (AddNoteStyle.css) for consistency across notes side panel, tag dropdown, and modal */
const TAG_STYLES: Record<NoteContextType, React.CSSProperties> = {
  [NoteContextType.APPOINTMENT]: {
    backgroundColor: 'var(--blue-200)',
    color: 'var(--blue-600)',
  },
  [NoteContextType.BILLING]: {
    backgroundColor: 'var(--violet-200)',
    color: 'var(--violet-700)',
  },
  [NoteContextType.CHARTING]: {
    backgroundColor: 'var(--blue-600)',
    color: 'var(--gray-white)',
  },
  [NoteContextType.TREATMENT_PLAN]: {
    backgroundColor: 'var(--indigo-200)',
    color: 'var(--indigo-700)',
  },
  [NoteContextType.CALL]: {
    backgroundColor: 'var(--indigo-200)',
    color: 'var(--indigo-700)',
  },
  [NoteContextType.CONTACT]: {
    backgroundColor: 'var(--indigo-200)',
    color: 'var(--indigo-700)',
  },
  [NoteContextType.PATIENT]: {
    backgroundColor: 'var(--vivid-green-200)',
    color: 'var(--vivid-green-700)',
  },
  [NoteContextType.PERIO]: {
    backgroundColor: 'var(--teal-200)',
    color: 'var(--teal-700)',
  },
  [NoteContextType.CLAIMS]: {
    backgroundColor: 'var(--orange-200)',
    color: 'var(--orange-700)',
  },
  [NoteContextType.CLAIM_ITEM]: {
    backgroundColor: 'var(--orange-200)',
    color: 'var(--orange-700)',
  },
  [NoteContextType.CAMPAIGN]: {
    backgroundColor: 'var(--indigo-200)',
    color: 'var(--indigo-700)',
  },
  [NoteContextType.FILES]: {
    backgroundColor: 'var(--gray-200)',
    color: 'var(--gray-700)',
  },
  [NoteContextType.XRAY]: {
    backgroundColor: 'var(--blue-200)',
    color: 'var(--blue-700)',
  },
  [NoteContextType.INSURANCE]: {
    backgroundColor: 'var(--pale-green-200)',
    color: 'var(--pale-green-700)',
  },
  [NoteContextType.PROVIDER]: {
    backgroundColor: 'var(--blue-200)',
    color: 'var(--blue-600)',
  },
  [NoteContextType.FORMS]: {
    backgroundColor: 'var(--pale-green-200)',
    color: 'var(--pale-green-700)',
  },
  [NoteContextType.CARRIER]: {
    backgroundColor: 'var(--orange-200)',
    color: 'var(--orange-700)',
  },
}

/** All note tag options. Driven by NoteContextType. */
export const TAG_OPTIONS: ReadonlyArray<{ text: string; value: string }> = (
  Object.values(NoteContextType) as NoteContextType[]
).map((value) => ({ text: CONTEXT_TYPE_LABELS[value], value }))

/** Context types hidden from the note tag dropdown (badges still render for API data). */
export const HIDDEN_TAG_OPTIONS: ReadonlySet<NoteContextType> = new Set([
  NoteContextType.CLAIM_ITEM,
  NoteContextType.PROVIDER,
  NoteContextType.FORMS,
  NoteContextType.CAMPAIGN,
  NoteContextType.FILES,
  NoteContextType.XRAY,
  NoteContextType.INSURANCE,
])

/** Tag options for the note editor dropdown (excludes hidden, sorted alphabetically by label). */
export const NOTE_TAG_OPTIONS: ReadonlyArray<{
  text: string
  value: string
}> = [
  ...TAG_OPTIONS.filter(
    (opt) => !HIDDEN_TAG_OPTIONS.has(opt.value as NoteContextType),
  ),
].sort((a, b) => a.text.localeCompare(b.text))

interface TagBadgeProps {
  tag: NoteContextType | string
  className?: string
}

const DEFAULT_TAG_STYLE: React.CSSProperties = {
  backgroundColor: 'var(--gray-200)',
  color: 'var(--gray-700)',
}

/** Display label for a note tag (e.g. in filters). Single source of truth with table badge. */
export function getNoteTagDisplayLabel(tag: NoteContextType | string): string {
  if (tag in CONTEXT_TYPE_LABELS) {
    return CONTEXT_TYPE_LABELS[tag as NoteContextType]
  }
  return String(tag)
}

function getDisplayLabel(tag: NoteContextType | string): string {
  return getNoteTagDisplayLabel(tag)
}

function getTagStyle(tag: NoteContextType | string): React.CSSProperties {
  if (typeof tag === 'string' && tag in TAG_STYLES) {
    return TAG_STYLES[tag as NoteContextType]
  }
  return DEFAULT_TAG_STYLE
}

const TagBadge: React.FC<TagBadgeProps> = ({ tag, className = '' }) => {
  const badgeStyle = useMemo(() => getTagStyle(tag), [tag])
  const displayLabel = useMemo(() => getDisplayLabel(tag), [tag])

  return (
    <span className={`tag-badge ${className}`} style={badgeStyle}>
      {displayLabel}
    </span>
  )
}

export default TagBadge
